import type { AirshowBoundingBox } from "./airshowReviewContract";

export type AirshowJunctionOperation =
  | "hard-union" | "smooth-union" | "fillet-proxy" | "aero-fairing";
export type AirshowJunctionSide = "both" | "positive-y" | "negative-y";
export type AirshowJunctionProfile = "constant" | "smoothstep" | "bell";

export interface AirshowJunctionControls {
  component_a: string | null;
  component_b: string | null;
  operation: AirshowJunctionOperation;
  blend_radius_source_units: number;
  side: AirshowJunctionSide;
  window_start: number;
  window_end: number;
  falloff: number;
  bias: number;
  profile: AirshowJunctionProfile;
  grid_resolution: number;
}

export interface AirshowJunctionSelection extends AirshowJunctionControls {
  component_a: string;
  component_b: string;
}

export interface AirshowJunctionPreviewRequest extends AirshowJunctionControls {
  schema: "loom.airshow-junction-preview-request/v1";
  entry_id: string;
  archive_member_path: string;
  source_sha256: string;
  review_bundle_key: string;
  review_resolution: 8;
}

export interface AirshowJunctionComponent {
  id: string;
  name: string;
  kind: string;
  bbox: AirshowBoundingBox;
}

export interface AirshowJunctionCandidate {
  id: string;
  component_a: string;
  component_b: string;
  label: string;
  evidence: "coarse-field-proximity";
  sides: readonly AirshowJunctionSide[];
}

export interface AirshowJunctionMeshMetrics {
  vertices: number;
  triangles: number;
  watertight: boolean;
  manifold: boolean;
}

export interface AirshowJunctionPreviewAvailable {
  status: "available";
  representation: "diagnostic-implicit-mesh";
  fidelity: "semantic-adapted-provisional";
  continuity: "not-certified";
  radius_semantics: "field-space-not-cad-radius";
  accepted: false;
  exportable: false;
  baseline_url: string;
  result_url: string;
  metadata_url: string;
  bbox: AirshowBoundingBox;
  baseline: AirshowJunctionMeshMetrics;
  result: AirshowJunctionMeshMetrics;
  warnings: readonly string[];
}

export interface AirshowJunctionPreviewUnavailable {
  status: "unavailable";
  representation: "unavailable";
  fidelity: "unavailable";
  code: string;
  message: string;
  retryable: boolean;
  warnings: readonly string[];
}

interface AirshowJunctionPreviewResponseBase {
  schema: "loom.airshow-junction-preview/v1";
  entry_id: string;
  archive_member_path: string;
  source_sha256: string;
  review_bundle_key: string;
  components: readonly AirshowJunctionComponent[];
  junctions: readonly AirshowJunctionCandidate[];
}

export interface AirshowJunctionPreviewAvailableResponse
  extends AirshowJunctionPreviewResponseBase {
  preview_bundle_key: string;
  selection: AirshowJunctionSelection;
  preview: AirshowJunctionPreviewAvailable;
}

export interface AirshowJunctionPreviewUnavailableResponse
  extends AirshowJunctionPreviewResponseBase {
  preview_bundle_key: null;
  selection: null;
  preview: AirshowJunctionPreviewUnavailable;
}

export type AirshowJunctionPreviewResponse =
  | AirshowJunctionPreviewAvailableResponse
  | AirshowJunctionPreviewUnavailableResponse;

type UnknownRow = Record<string, unknown>;
const OPERATIONS: readonly AirshowJunctionOperation[] = [
  "hard-union", "smooth-union", "fillet-proxy", "aero-fairing",
];
const SIDES: readonly AirshowJunctionSide[] = ["both", "positive-y", "negative-y"];
const PROFILES: readonly AirshowJunctionProfile[] = ["constant", "smoothstep", "bell"];
const CONTROL_KEYS = [
  "component_a", "component_b", "operation", "blend_radius_source_units", "side",
  "window_start", "window_end", "falloff", "bias", "profile", "grid_resolution",
] as const;

function fail(message: string): never {
  throw new Error(`Airshow junction preview ${message}`);
}

function row(value: unknown, path: string): UnknownRow {
  if (!value || typeof value !== "object" || Array.isArray(value)) fail(`${path} must be an object`);
  return value as UnknownRow;
}

function exactKeys(value: UnknownRow, keys: readonly string[], path: string): void {
  const actual = Object.keys(value);
  if (actual.length !== keys.length || actual.some((key) => !keys.includes(key))) {
    fail(`${path} has an unsupported field set`);
  }
}

function text(value: unknown, path: string, maximum = 256): string {
  if (typeof value !== "string" || !value.trim() || value.length > maximum) {
    fail(`${path} must be bounded non-empty text`);
  }
  return value;
}

function digest(value: unknown, path: string): string {
  const result = text(value, path, 80);
  if (!/^[a-f0-9]{64}$/.test(result)) fail(`${path} must be a bare SHA-256 identity`);
  return result;
}

function finite(value: unknown, path: string, minimum: number, maximum: number): number {
  if (typeof value !== "number" || !Number.isFinite(value) || value < minimum || value > maximum) {
    fail(`${path} is outside its numeric domain`);
  }
  return value;
}

function integer(value: unknown, path: string, minimum: number, maximum: number): number {
  const result = finite(value, path, minimum, maximum);
  if (!Number.isInteger(result)) fail(`${path} must be an integer`);
  return result;
}

function bbox(value: unknown, path: string): AirshowBoundingBox {
  if (!Array.isArray(value) || value.length !== 2) fail(`${path} must contain two corners`);
  const parseCorner = (corner: unknown, index: number): readonly [number, number, number] => {
    if (!Array.isArray(corner) || corner.length !== 3) fail(`${path}[${index}] must contain three coordinates`);
    return [
      finite(corner[0], `${path}[${index}][0]`, -1e12, 1e12),
      finite(corner[1], `${path}[${index}][1]`, -1e12, 1e12),
      finite(corner[2], `${path}[${index}][2]`, -1e12, 1e12),
    ];
  };
  const corners = [parseCorner(value[0], 0), parseCorner(value[1], 1)] as const;
  if (corners[0].some((minimum, index) => minimum > corners[1][index]!)) fail(`${path} is inverted`);
  return corners;
}

function warnings(value: unknown, path: string): readonly string[] {
  if (!Array.isArray(value) || value.length > 16) fail(`${path} must contain at most 16 warnings`);
  return value.map((item, index) => text(item, `${path}[${index}]`, 512));
}

function componentId(value: unknown, path: string): string {
  const result = text(value, path, 128);
  if (!/^component-[0-9]{4}$/.test(result)) fail(`${path} is not a canonical component id`);
  return result;
}

function junctionId(value: unknown, path: string): string {
  const result = text(value, path, 160);
  if (!/^junction-component-[0-9]{4}-component-[0-9]{4}$/.test(result)) {
    fail(`${path} is not a canonical junction id`);
  }
  return result;
}

function kind(value: unknown, path: string): string {
  const result = text(value, path, 64);
  if (!/^[a-z][a-z0-9-]{0,63}$/.test(result)) fail(`${path} is not a canonical component kind`);
  return result;
}

function code(value: unknown, path: string): string {
  const result = text(value, path, 128);
  if (!/^[a-z0-9][a-z0-9-]{0,127}$/.test(result)) fail(`${path} is not a canonical code`);
  return result;
}

function parseControls(value: unknown, path: string, nullable: boolean): AirshowJunctionControls {
  const source = row(value, path);
  exactKeys(source, CONTROL_KEYS, path);
  const componentA = source.component_a === null && nullable
    ? null : componentId(source.component_a, `${path}.component_a`);
  const componentB = source.component_b === null && nullable
    ? null : componentId(source.component_b, `${path}.component_b`);
  if ((componentA === null) !== (componentB === null) || (componentA !== null && componentA === componentB)) {
    fail(`${path} component pair must be both null or distinct`);
  }
  if (!OPERATIONS.includes(source.operation as AirshowJunctionOperation)) fail(`${path}.operation is unsupported`);
  if (!SIDES.includes(source.side as AirshowJunctionSide)) fail(`${path}.side is unsupported`);
  if (!PROFILES.includes(source.profile as AirshowJunctionProfile)) fail(`${path}.profile is unsupported`);
  const windowStart = finite(source.window_start, `${path}.window_start`, 0, 1);
  const windowEnd = finite(source.window_end, `${path}.window_end`, 0, 1);
  if (windowStart >= windowEnd) fail(`${path} window must have start before end`);
  return {
    component_a: componentA,
    component_b: componentB,
    operation: source.operation as AirshowJunctionOperation,
    blend_radius_source_units: finite(
      source.blend_radius_source_units, `${path}.blend_radius_source_units`, 0, 1_000_000,
    ),
    side: source.side as AirshowJunctionSide,
    window_start: windowStart,
    window_end: windowEnd,
    falloff: finite(source.falloff, `${path}.falloff`, 0, 0.5),
    bias: finite(source.bias, `${path}.bias`, -1, 1),
    profile: source.profile as AirshowJunctionProfile,
    grid_resolution: integer(source.grid_resolution, `${path}.grid_resolution`, 24, 64),
  };
}

function parseComponent(value: unknown, index: number): AirshowJunctionComponent {
  const path = `response.components[${index}]`;
  const source = row(value, path);
  exactKeys(source, ["id", "name", "kind", "bbox"], path);
  return {
    id: componentId(source.id, `${path}.id`),
    name: text(source.name, `${path}.name`),
    kind: kind(source.kind, `${path}.kind`),
    bbox: bbox(source.bbox, `${path}.bbox`),
  };
}

function parseJunction(value: unknown, index: number): AirshowJunctionCandidate {
  const path = `response.junctions[${index}]`;
  const source = row(value, path);
  exactKeys(source, ["id", "component_a", "component_b", "label", "evidence", "sides"], path);
  if (source.evidence !== "coarse-field-proximity") fail(`${path}.evidence is unsupported`);
  if (!Array.isArray(source.sides) || source.sides.length !== SIDES.length
      || source.sides.some((side, sideIndex) => side !== SIDES[sideIndex])) {
    fail(`${path}.sides is unsupported`);
  }
  const id = junctionId(source.id, `${path}.id`);
  const componentA = componentId(source.component_a, `${path}.component_a`);
  const componentB = componentId(source.component_b, `${path}.component_b`);
  if (componentA >= componentB) fail(`${path} component pair is not canonically ordered`);
  if (id !== `junction-${componentA}-${componentB}`) {
    fail(`${path}.id does not match its component pair`);
  }
  return {
    id,
    component_a: componentA,
    component_b: componentB,
    label: text(source.label, `${path}.label`),
    evidence: "coarse-field-proximity",
    sides: source.sides as AirshowJunctionSide[],
  };
}

function parseMetrics(value: unknown, path: string): AirshowJunctionMeshMetrics {
  const source = row(value, path);
  exactKeys(source, ["vertices", "triangles", "watertight", "manifold"], path);
  if (typeof source.watertight !== "boolean" || typeof source.manifold !== "boolean") {
    fail(`${path} topology flags must be boolean`);
  }
  return {
    vertices: integer(source.vertices, `${path}.vertices`, 0, 5_000_000),
    triangles: integer(source.triangles, `${path}.triangles`, 0, 5_000_000),
    watertight: source.watertight,
    manifold: source.manifold,
  };
}

function artifactUrl(value: unknown, projectId: string, bundleKey: string, name: string): string {
  const result = text(value, `response.preview.${name}_url`, 1024);
  const expected = `/api/thread/v1/projects/${encodeURIComponent(projectId)}`
    + `/aero/vsp/airshow/junction-previews/${bundleKey}/${name === "metadata" ? "metadata.json" : `${name}.gltf`}`;
  if (result !== expected) fail(`response.preview.${name}_url is not bound to its preview bundle`);
  return result;
}

function parseAvailablePreview(
  value: UnknownRow,
  projectId: string,
  bundleKey: string,
): AirshowJunctionPreviewAvailable {
  const keys = [
    "status", "representation", "fidelity", "continuity", "radius_semantics", "accepted",
    "exportable", "baseline_url", "result_url", "metadata_url", "bbox", "baseline", "result", "warnings",
  ];
  exactKeys(value, keys, "response.preview");
  if (value.status !== "available" || value.representation !== "diagnostic-implicit-mesh"
      || value.fidelity !== "semantic-adapted-provisional" || value.continuity !== "not-certified"
      || value.radius_semantics !== "field-space-not-cad-radius"
      || value.accepted !== false || value.exportable !== false) {
    fail("response.preview makes an unsupported fidelity claim");
  }
  return {
    status: "available",
    representation: "diagnostic-implicit-mesh",
    fidelity: "semantic-adapted-provisional",
    continuity: "not-certified",
    radius_semantics: "field-space-not-cad-radius",
    accepted: false,
    exportable: false,
    baseline_url: artifactUrl(value.baseline_url, projectId, bundleKey, "baseline"),
    result_url: artifactUrl(value.result_url, projectId, bundleKey, "result"),
    metadata_url: artifactUrl(value.metadata_url, projectId, bundleKey, "metadata"),
    bbox: bbox(value.bbox, "response.preview.bbox"),
    baseline: parseMetrics(value.baseline, "response.preview.baseline"),
    result: parseMetrics(value.result, "response.preview.result"),
    warnings: warnings(value.warnings, "response.preview.warnings"),
  };
}

function parseUnavailablePreview(value: UnknownRow): AirshowJunctionPreviewUnavailable {
  exactKeys(
    value,
    ["status", "representation", "fidelity", "code", "message", "retryable", "warnings"],
    "response.preview",
  );
  if (value.status !== "unavailable" || value.representation !== "unavailable"
      || value.fidelity !== "unavailable" || typeof value.retryable !== "boolean") {
    fail("response.preview unavailable state is contradictory");
  }
  return {
    status: "unavailable",
    representation: "unavailable",
    fidelity: "unavailable",
    code: code(value.code, "response.preview.code"),
    message: text(value.message, "response.preview.message", 1024),
    retryable: value.retryable,
    warnings: warnings(value.warnings, "response.preview.warnings"),
  };
}

function controlsEqual(selection: AirshowJunctionSelection, request: AirshowJunctionPreviewRequest): boolean {
  return CONTROL_KEYS.every((key) => (
    key === "component_a" || key === "component_b"
      ? request[key] === null || request[key] === selection[key]
      : request[key] === selection[key]
  ));
}

export function createAirshowJunctionPreviewRequest(input: {
  identity: { entryId: string; archiveMemberPath: string; sourceSha256: string; reviewBundleKey: string };
  controls?: Partial<AirshowJunctionControls>;
}): AirshowJunctionPreviewRequest {
  const request = {
    schema: "loom.airshow-junction-preview-request/v1" as const,
    entry_id: text(input.identity.entryId, "request.entry_id"),
    archive_member_path: text(input.identity.archiveMemberPath, "request.archive_member_path", 512),
    source_sha256: digest(input.identity.sourceSha256, "request.source_sha256"),
    review_bundle_key: digest(input.identity.reviewBundleKey, "request.review_bundle_key"),
    review_resolution: 8 as const,
    component_a: null,
    component_b: null,
    operation: "hard-union" as AirshowJunctionOperation,
    blend_radius_source_units: 0,
    side: "both" as AirshowJunctionSide,
    window_start: 0,
    window_end: 1,
    falloff: 0.1,
    bias: 0,
    profile: "smoothstep" as AirshowJunctionProfile,
    grid_resolution: 48,
    ...input.controls,
  };
  const controls = parseControls(
    Object.fromEntries(CONTROL_KEYS.map((key) => [key, request[key]])),
    "request",
    true,
  );
  return { ...request, ...controls };
}

export function parseAirshowJunctionPreviewResponse(
  value: unknown,
  expected: { projectId: string; request: AirshowJunctionPreviewRequest },
): AirshowJunctionPreviewResponse {
  const source = row(value, "response");
  exactKeys(source, [
    "schema", "entry_id", "archive_member_path", "source_sha256", "review_bundle_key",
    "preview_bundle_key", "components", "junctions", "selection", "preview",
  ], "response");
  const request = expected.request;
  if (source.schema !== "loom.airshow-junction-preview/v1"
      || source.entry_id !== request.entry_id || source.archive_member_path !== request.archive_member_path
      || source.source_sha256 !== request.source_sha256 || source.review_bundle_key !== request.review_bundle_key) {
    fail("response identity does not match its request");
  }
  if (!Array.isArray(source.components) || source.components.length > 64
      || !Array.isArray(source.junctions) || source.junctions.length > 256) {
    fail("response catalog exceeds its bounded list sizes");
  }
  const components = source.components.map(parseComponent);
  const componentIds = components.map(({ id }) => id);
  if (new Set(componentIds).size !== componentIds.length) fail("response components have duplicate identities");
  const junctions = source.junctions.map(parseJunction);
  const junctionIds = junctions.map(({ id }) => id);
  const junctionPairs = junctions.map(({ component_a: a, component_b: b }) => `${a}\0${b}`);
  if (new Set(junctionIds).size !== junctionIds.length
      || new Set(junctionPairs).size !== junctionPairs.length
      || junctions.some(({ component_a: a, component_b: b }) => (
        !componentIds.includes(a) || !componentIds.includes(b)
      ))) fail("response junction topology is invalid");
  const selection = source.selection === null ? null
    : parseControls(source.selection, "response.selection", false) as AirshowJunctionSelection;
  if (selection && (!componentIds.includes(selection.component_a)
      || !componentIds.includes(selection.component_b) || !controlsEqual(selection, request))) {
    fail("response selection contradicts its request or component catalog");
  }
  if (selection && !junctions.some(({ component_a: a, component_b: b }) => (
    (a === selection.component_a && b === selection.component_b)
    || (b === selection.component_a && a === selection.component_b)
  ))) fail("response selection is not bound to a declared junction");
  const previewBundleKey = source.preview_bundle_key === null
    ? null : digest(source.preview_bundle_key, "response.preview_bundle_key");
  const previewSource = row(source.preview, "response.preview");
  const preview = previewSource.status === "available"
    ? (previewBundleKey ? parseAvailablePreview(previewSource, expected.projectId, previewBundleKey)
      : fail("available response has no preview bundle"))
    : parseUnavailablePreview(previewSource);
  const identity = {
    schema: "loom.airshow-junction-preview/v1" as const,
    entry_id: request.entry_id,
    archive_member_path: request.archive_member_path,
    source_sha256: request.source_sha256,
    review_bundle_key: request.review_bundle_key,
    components,
    junctions,
  };
  if (preview.status === "available") {
    if (!previewBundleKey || !selection) {
      fail("available response has no resolved selection and preview bundle");
    }
    return { ...identity, preview_bundle_key: previewBundleKey, selection, preview };
  }
  if (previewBundleKey !== null || selection !== null) {
    fail("unavailable response must not claim a selection or preview bundle");
  }
  return { ...identity, preview_bundle_key: null, selection: null, preview };
}
